import { useEffect, useState } from "react";
export const getValidityState = (validationMessage, warningMessage) => {
  if (validationMessage) {
    return "error";
  } else if (warningMessage) {
    return "warning";
  } else if (validationMessage === "") {
    return "success";
  } else {
    return "unknown";
  }
};

/**
 * Used to hook into native validation for a form control.
 */
export default function useValidation(id, onValidate) {
  const [validationMessage, setValidationMessage] = useState();
  const [warningMessage, setCustomWarning] = useState();
  const reset = () => {
    setValidationMessage(undefined);
    setCustomWarning(undefined);
  };
  useEffect(() => {
    const input = document.getElementById(id);
    if (input) {
      //Exposes the reset method via the input
      input._edenReset = reset;
      if (input.type === "fieldset") {
        //fieldset's native `setCustomValidity` does not update the `validationMessage` property. This accounts for that by setting a private property instead.
        input.setCustomValidity = new Proxy(input.setCustomValidity, {
          apply: (target, that, args) => {
            input._edenValidationMessage = args.join("\n");
            target.apply(that, args);
          }
        });
        //Use defineProperty as the `validationMessage` property is not overridable.
        Reflect.defineProperty(input, "validationMessage", {
          get() {
            var _find;
            return input._edenValidationMessage || ((_find = [...input.elements].find(elem => !!elem.validationMessage)) === null || _find === void 0 ? void 0 : _find.validationMessage) || "";
          }
        });
      }
    }
  }, [id]);
  useEffect(() => {
    const input = document.getElementById(id);
    if (input) {
      //disable validation
      if (!input.form || input.form.dataset.edenFormNoValidate) {
        reset();
        return;
      }
      const checkValidity = event => {
        //make sure component has finished updating before checking validity.
        requestAnimationFrame(() => {
          const target = event.target;
          target.checkValidity ? target.checkValidity() : input.checkValidity();
          updateValidationMessage(event);
        });
      };
      const updateValidationMessage = () => {
        onValidate === null || onValidate === void 0 ? void 0 : onValidate(input, {
          setCustomWarning,
          ...(input._edenOnValidateDetails ?? {}) //_edenOnValidateDetails is set per form control that needs extra helper methods.
        });

        setValidationMessage(input.validationMessage);
      };
      const inputHandler = event => {
        if (validationMessage !== undefined) {
          checkValidity(event);
        }
      };

      //Events are handled on the capture phase due to certain events not bubbling.
      input.addEventListener("input", inputHandler, true);
      input.addEventListener("change", checkValidity, true);
      input.addEventListener("validate", checkValidity, true);
      input.addEventListener("invalid", updateValidationMessage, true);
      return () => {
        input.removeEventListener("input", inputHandler, true);
        input.removeEventListener("change", checkValidity, true);
        input.removeEventListener("validate", checkValidity, true);
        input.removeEventListener("invalid", updateValidationMessage, true);
      };
    }
  }, [id, validationMessage, onValidate]);
  return {
    validationMessage,
    warningMessage
  };
}